import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Activity, Loader2, Building2, Plus, Trash2, Eraser, ChevronDown, ChevronUp, AlertTriangle, Download, FileJson, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from '../config/api';
import jsPDF from 'jspdf';
import TabPageLayout, { PageLoader, DataCard } from "./TabPageLayout";

import * as tests from "./health/testDefinitions";
import { ProgressChart } from "./health/ProgressChart";
import { PreFlightStatus } from "./health/PreFlightStatus";
import { FoundationSection } from "./health/FoundationSection";
import { OperationsSection } from "./health/OperationsSection";
import { FinancialsSection } from "./health/FinancialsSection";
import { CustomersSection } from "./health/CustomersSection";
import { SettingsSection } from "./health/SettingsSection";
import { AuthFlowsSection } from "./health/AuthFlowsSection";
import { CustomerFlowsSection } from "./health/CustomerFlowsSection";
// Enterprise Test Sections
import { IntegritySection } from "./health/IntegritySection";
import { NegativePathSection } from "./health/NegativePathSection";
import { AutomationSection } from "./health/AutomationSection";
import { AdvancedSection } from "./health/AdvancedSection";
// Login & Permissions Test Section
import { LoginSection } from "./health/LoginSection";
// Employees Test Section
import { EmployeesSection } from "./health/EmployeesSection";
// Security & Hardening Test Section
import { SecurityHardeningSection } from "./health/SecurityHardeningSection";
// Partners & Affiliates Test Section
import { PartnersAffiliatesSection } from "./health/PartnersAffiliatesSection";
// Real-Time Availability Test Section
import { RealtimeAvailabilitySection } from "./health/RealtimeAvailabilitySection";
// Widget Checkout Test Section
import { WidgetCheckoutSection } from "./health/WidgetCheckoutSection";
// Widget JS Quality Test Section (iOS Safari Bug Fix)
import { WidgetJsQualitySection } from "./health/WidgetJsQualitySection";
// Service Sharing Test Section
import { ServiceSharingSection } from "./health/ServiceSharingSection";
// Email Notification Test Section (Cron Job & Owner Notifications)
import { EmailNotificationSection } from "./health/EmailNotificationSection";
// Platform Administration Test Section (Jan 2026)
import { PlatformAdminSection } from "./health/PlatformAdminSection";
// Data Integrity & Audit Section (Jan 2026)
import { DataIntegrityAuditSection } from "./health/DataIntegrityAuditSection";

// Import new test definitions (Jan 2026)
import { crossServiceAvailabilityTests, dataIntegrityTests, financialsDataTests, timezoneConsistencyTests, emailDataIntegrityTests } from './health/testDefinitions';

// Active Issues Box Component
const ActiveIssuesBox = ({ testResults }) => {
  const allTests = [
    // Foundation
    ...(testResults.orgLifecycle || []), ...(testResults.multiTenant || []), ...(testResults.countryConfig || []), ...(testResults.serviceWizard || []), ...(testResults.bookingAvailability || []),
    // Operations
    ...testResults.roles, ...testResults.team, ...testResults.assets,
    ...testResults.services, ...testResults.bookings, ...(testResults.customBookings || []), ...(testResults.customRequests || []), ...testResults.flows,
    // Financials
    ...testResults.revenue, ...(testResults.revenueDashboard || []), ...testResults.payouts, ...testResults.account,
    ...testResults.payroll, ...testResults.stripeConnect, ...(testResults.expenses || []),
    // Customers
    ...testResults.branding, ...testResults.waivers, ...testResults.reviews,
    ...testResults.checkin, ...testResults.leads, ...testResults.relations, ...(testResults.tickets || []),
    // Settings
    ...testResults.profile, ...testResults.preferences, ...testResults.calendar,
    ...testResults.webhooks, ...testResults.apiKeys, ...testResults.activityLog, ...(testResults.widgetApi || []), ...(testResults.currencyConsistency || []), ...(testResults.multiCurrencyCheckout || []), ...(testResults.addOnsData || []), ...(testResults.customerTicket || []), ...(testResults.integrations || []),
    // Auth Flows
    ...testResults.ownerSignin, ...testResults.teamSignin, ...testResults.signup,
    // Customer Flows
    ...testResults.widgetCheckout, ...testResults.waiverFlow, ...testResults.qrCodeScanning,
    // Enterprise: Integrity
    ...(testResults.doubleBooking || []), ...(testResults.feeCalculations || []),
    ...(testResults.dataConsistency || []),
    // Enterprise: Negative Paths
    ...(testResults.validation || []), ...(testResults.security || []), ...(testResults.errorHandling || []),
    // Enterprise: Automation
    ...(testResults.emailAutomation || []), ...(testResults.alerts || []), ...(testResults.retryLogic || []),
    // Enterprise: Advanced
    ...(testResults.widgetEmbed || []), ...(testResults.stressTest || []), ...(testResults.ciReadiness || []),
    // Login & Permissions
    ...(testResults.superEasyLogin || []), ...(testResults.promoCodePermissions || []),
    // Internal Booking System (TDD)
    ...(testResults.internalBookingFees || []), ...(testResults.internalBookingUI || []),
    ...(testResults.internalBookingData || []), ...(testResults.internalBookingValidation || []),
    ...(testResults.internalBookingPaymentOptions || []), ...(testResults.internalBookingModifications || []),
    ...(testResults.internalBookingCancellations || []),
    // Session Fixes (Dec 2025)
    ...(testResults.sessionEmailFormatting || []), ...(testResults.sessionPayNowRedirect || []),
    ...(testResults.sessionCapacityCalculation || []), ...(testResults.sessionUICalendar || []),
    // New Test Suites (Dec 2025)
    ...(testResults.directCharges || []), ...(testResults.refundSecurity || []),
    ...(testResults.multipleRefunds || []), ...(testResults.emailNotifications || []),
    ...(testResults.dataMigration || []),
    // Advanced Security & Hardening (Dec 2025)
    ...(testResults.rbacMatrix || []), ...(testResults.webhookSecurity || []),
    ...(testResults.bookingMutations || []), ...(testResults.concurrency || []),
    ...(testResults.piiSecurity || []), ...(testResults.tenantIsolation || []),
    // Partners & Affiliates (Dec 2025)
    ...(testResults.partnersAffiliates || []),
    ...(testResults.partnerEarnings || []),
    // Real-Time Availability (Dec 2025)
    ...(testResults.realtimeAvailability || []),
    // Service Sharing (Dec 2025)
    ...(testResults.serviceSharing || []),
    // Widget JS Quality - iOS Safari Bug Fix (Dec 2025)
    ...(testResults.widgetJsQuality || []),
    // Platform Administration (Jan 2026)
    ...(testResults.platformAdmin || []),
    // Cross-Service Availability (Jan 2026)
    ...(testResults.crossServiceAvailability || []),
    // Data Integrity (Jan 2026)
    ...(testResults.dataIntegrity || []),
    // Financials P&L (Jan 2026)
    ...(testResults.financialsData || []),
    // Timezone Consistency (Jan 2026)
    ...(testResults.timezoneConsistency || []),
    // Email Data Integrity (Jan 2026)
    ...(testResults.emailDataIntegrity || [])
  ];

  const failedTests = allTests.filter(t => t.status === 'fail');
  
  if (failedTests.length === 0) return null;

  return (
    <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-5 w-5 text-red-600" />
        <span className="font-semibold text-red-900">Active Issues ({failedTests.length})</span>
      </div>
      <div className="space-y-2">
        {failedTests.slice(0, 5).map((test, idx) => (
          <div key={idx} className="flex items-start gap-2 text-sm">
            <span className="text-red-500 mt-0.5">•</span>
            <div>
              <span className="font-medium text-red-800">{test.name}</span>
              {test.message && (
                <span className="text-red-600 ml-1">→ {test.message}</span>
              )}
            </div>
          </div>
        ))}
        {failedTests.length > 5 && (
          <div className="text-xs text-red-600 italic mt-2">
            +{failedTests.length - 5} more issues...
          </div>
        )}
      </div>
    </div>
  );
};

// Format org data counts as readable labels
const formatDataCounts = (dataCounts) => {
  if (!dataCounts) return "";
  return `Roles: ${dataCounts.roles || 0} · Team: ${dataCounts.team_members || 0} · Assets: ${dataCounts.assets || 0} · Services: ${dataCounts.services || 0} · Bookings: ${dataCounts.bookings || 0}`;
};

// Format timestamp with timezone context
const formatTimestamp = (date) => {
  if (!date) return "";
  const localTime = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone?.split('/').pop() || 'Local';
  const utcTime = date.toUTCString().slice(17, 25);
  return `${localTime} ${localTz} (${utcTime} UTC)`;
};

const SystemHealthTab = () => {
  const [testResults, setTestResults] = useState({
    // Foundation
    orgLifecycle: [],
    multiTenant: [],
    countryConfig: [],
    // Operations
    roles: [],
    team: [],
    assets: [],
    services: [],
    servicesApi: [],
    bookings: [],
    customBookings: [],
    customRequests: [],
    accountDeletion: [],
    flows: [],
    // Financials
    revenue: [],
    revenueDashboard: [],
    payouts: [],
    account: [],
    payroll: [],
    stripeConnect: [],
    promoCodes: [],
    expenses: [],
    // Customers
    branding: [],
    waivers: [],
    reviews: [],
    checkin: [],
    leads: [],
    relations: [],
    tickets: [],
    // Settings
    profile: [],
    preferences: [],
    calendar: [],
    webhooks: [],
    apiKeys: [],
    activityLog: [],
    // Auth Flows
    ownerSignin: [],
    teamSignin: [],
    signup: [],
    // Customer Flows
    widgetCheckout: [],
    waiverFlow: [],
    qrCodeScanning: [],
    // Enterprise: Integrity
    doubleBooking: [],
    feeCalculations: [],
    dataConsistency: [],
    // Enterprise: Negative Paths
    validation: [],
    security: [],
    errorHandling: [],
    // Enterprise: Automation
    emailAutomation: [],
    alerts: [],
    retryLogic: [],
    // Enterprise: Advanced
    widgetEmbed: [],
    stressTest: [],
    ciReadiness: [],
    // Login & Permissions
    superEasyLogin: [],
    promoCodePermissions: [],
    // Internal Booking System (TDD - Most will fail until implemented)
    internalBookingFees: [],
    internalBookingUI: [],
    internalBookingData: [],
    internalBookingValidation: [],
    internalBookingPaymentOptions: [],
    internalBookingModifications: [],
    internalBookingCancellations: [],
    // Session Fixes (Dec 2025)
    sessionEmailFormatting: [],
    sessionPayNowRedirect: [],
    sessionCapacityCalculation: [],
    sessionUICalendar: [],
    // Employees (Dec 2025)
    timeclockSystem: [],
    twoFactorSecurity: [],
    // New Test Suites (Dec 2025)
    directCharges: [],
    refundSecurity: [],
    multipleRefunds: [],
    emailNotifications: [],
    dataMigration: [],
    // Advanced Security & Hardening (Dec 2025)
    rbacMatrix: [],
    webhookSecurity: [],
    bookingMutations: [],
    concurrency: [],
    piiSecurity: [],
    tenantIsolation: [],
    // Partners & Affiliates (Dec 2025)
    partnersAffiliates: [],
    partnerEarnings: [],
    // Real-Time Availability (Dec 2025)
    realtimeAvailability: [],
    // Widget Checkout Fixes (Dec 2025)
    widgetCheckoutFixes: [],
    // Widget JS Quality Tests (Dec 2025 - iOS Safari Bug Fix)
    widgetJsQuality: [],
    // Platform Administration (Jan 2026)
    platformAdmin: [],
    // Cross-Service Availability (Jan 2026)
    crossServiceAvailability: [],
    // Data Integrity (Jan 2026)
    dataIntegrity: [],
    // Financials P&L (Jan 2026)
    financialsData: [],
    // Timezone Consistency (Jan 2026)
    timezoneConsistency: [],
    // Email Data Integrity (Jan 2026)
    emailDataIntegrity: [],
    // Service Sharing (Dec 2025)
    serviceSharing: []
  });
  const [loading, setLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [addDataLoading, setAddDataLoading] = useState(false);
  const [deleteTestLoading, setDeleteTestLoading] = useState(false);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [testOrgsList, setTestOrgsList] = useState([]);
  const [selectedTestOrg, setSelectedTestOrg] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [testOrgInfo, setTestOrgInfo] = useState(null);
  const [testDuration, setTestDuration] = useState(null); // Time in seconds
  const [showExportMenu, setShowExportMenu] = useState(false);
  
  // Determine if any operation is running for disable states
  const isOperationRunning = loading || cleanupLoading || addDataLoading || deleteTestLoading || createOrgLoading;

  // Export function for test results
  const exportResults = (format) => {
    // Collect all test results
    const allTests = [];
    Object.entries(testResults).forEach(([category, tests]) => {
      if (Array.isArray(tests)) {
        tests.forEach(test => {
          allTests.push({
            category,
            name: test.name,
            status: test.status,
            message: test.message || ''
          });
        });
      }
    });

    const passed = allTests.filter(t => t.status === 'pass').length;
    const failed = allTests.filter(t => t.status === 'fail').length;
    const warnings = allTests.filter(t => t.status === 'warn').length;
    const timestamp = new Date().toISOString();

    if (format === 'json') {
      const data = {
        exportedAt: timestamp,
        testOrg: selectedTestOrg,
        summary: { total: allTests.length, passed, failed, warnings },
        duration: testDuration,
        tests: allTests
      };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-report-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON exported');
    } else if (format === 'csv') {
      const headers = ['Category', 'Test Name', 'Status', 'Message'];
      const rows = allTests.map(t => [t.category, t.name, t.status, t.message.replace(/,/g, ';')]);
      const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health-report-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } else if (format === 'pdf') {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text('System Health Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
      doc.text(`Test Org: ${selectedTestOrg || 'N/A'}`, 20, 36);
      doc.text(`Duration: ${testDuration || 'N/A'}s`, 20, 42);
      doc.setFontSize(12);
      doc.text(`Summary: ${passed} Passed | ${failed} Failed | ${warnings} Warnings`, 20, 52);
      
      let y = 65;
      doc.setFontSize(9);
      const failedTests = allTests.filter(t => t.status === 'fail');
      if (failedTests.length > 0) {
        doc.setFontSize(11);
        doc.text('Failed Tests:', 20, y);
        y += 8;
        doc.setFontSize(8);
        failedTests.slice(0, 30).forEach(t => {
          if (y > 280) { doc.addPage(); y = 20; }
          doc.text(`• [${t.category}] ${t.name}`, 22, y);
          y += 5;
        });
      }
      
      doc.save(`health-report-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported');
    }
  };

  // Load test orgs on mount
  useEffect(() => {
    loadTestOrgs();
  }, []);

  const loadTestOrgs = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.get(`${API_URL}/health/list-test-orgs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTestOrgsList(response.data.testOrgs);
        if (response.data.testOrgs.length > 0 && !selectedTestOrg) {
          setSelectedTestOrg(response.data.testOrgs[0].slug);
        }
      }
    } catch (error) {
      console.error("Error loading test orgs:", error);
    }
  };

  const createTestOrg = async () => {
    setCreateOrgLoading(true);
    toast.info('Creating test organization...');
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${API_URL}/health/create-test-org`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const org = response.data.testOrg;
        toast.success(`Created ${org.slug}!`);
        await loadTestOrgs(); // Refresh list
        setSelectedTestOrg(org.slug); // Auto-select new org
      }
    } catch (error) {
      console.error("Create org error:", error);
      toast.error("Failed to create test org");
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const addTestData = async () => {
    setAddDataLoading(true);
    toast.info('Adding test data...');
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${API_URL}/health/add-test-data`,
        { org_slug: selectedTestOrg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const created = response.data.created;
        toast.success(
          `Added ${created.roles} roles, ${created.team_members} team, ${created.assets} assets, ${created.services} services, ${created.bookings} bookings`
        );
        await loadTestOrgs(); // Refresh org list
      }
    } catch (error) {
      console.error("Add data error:", error);
      toast.error("Failed to add test data");
    } finally {
      setAddDataLoading(false);
    }
  };

  const testDeleteOperations = async () => {
    if (!selectedTestOrg) {
      toast.error("Please select a test org first");
      return;
    }
    
    setDeleteTestLoading(true);
    toast.info('Testing delete operations...');
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${API_URL}/health/test-delete-operations/${selectedTestOrg}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const summary = response.data.summary;
        toast.success(`Delete tests: ${summary.passed}/${summary.total} passed`);
      }
    } catch (error) {
      console.error("Delete test error:", error);
      toast.error("Failed to test delete operations");
    } finally {
      setDeleteTestLoading(false);
    }
  };

  const runHealthCheck = async () => {
    if (!selectedTestOrg) {
      toast.error("Please create or select a test org first");
      return;
    }
    
    setLoading(true);
    setTestDuration(null); // Reset duration
    const startTime = performance.now(); // Start timing
    
    try {
      const token = localStorage.getItem("jwtToken");
      
      // STEP 0: Run pre-flight checks FIRST
      toast.info('Running pre-flight checks...');
      const preFlightResponse = await axios.get(`${API_URL}/health/pre-flight`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!preFlightResponse.data.preFlightPassed) {
        toast.error('CRITICAL: Pre-flight checks failed! Fix infrastructure first.');
        setLoading(false);
        return;
      }
      
      toast.success('Pre-flight checks passed!');
      toast.info('Running test suites...');
      
      // Helper function to call endpoint with fallback
      const callEndpoint = async (endpoint, testList) => {
        try {
          // Pass the selected test org to the backend
          const response = await axios.post(`${API_URL}/health/test-suites/${endpoint}`, {
            org_slug: selectedTestOrg
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return response;
        } catch (err) {
          // Return mock failed tests if endpoint not implemented
          return {
            data: {
              tests: testList.map(t => ({ 
                name: t.name, 
                status: 'fail', 
                message: 'Test suite not implemented yet' 
              })),
              summary: { passed: 0, failed: testList.length }
            }
          };
        }
      };

      // Run all test suites in parallel
      const [
        // Foundation
        orgLifecycleResponse,
        multiTenantResponse,
        countryConfigResponse,
        serviceWizardResponse,
        bookingAvailabilityResponse,
        // Operations
        rolesResponse,
        teamResponse,
        assetsResponse,
        servicesResponse,
        servicesApiResponse,
        bookingsResponse,
        customBookingsResponse,
        customRequestsResponse,
        accountDeletionResponse,
        flowsResponse,
        // Financials
        revenueResponse,
        revenueDashboardResponse,
        payoutsResponse,
        accountResponse,
        payrollResponse,
        promoCodesResponse,
        stripeConnectResponse,
        expensesResponse,
        // Customers
        brandingResponse,
        waiversResponse,
        reviewsResponse,
        checkinResponse,
        leadsResponse,
        leadsConsistencyResponse,
        relationsResponse,
        ticketsResponse,
        // Settings
        profileResponse,
        preferencesResponse,
        calendarResponse,
        webhooksResponse,
        apiKeysResponse,
        activityLogResponse, widgetApiResponse, currencyResponse, multiCurrencyCheckoutResponse, addOnsDataResponse, customerTicketResponse, integrationsResponse,
        // Auth Flows
        ownerSigninResponse,
        teamSigninResponse,
        signupResponse,
        // Customer Flows
        widgetCheckoutResponse,
        waiverFlowResponse,
        qrCodeScanningResponse,
        // Enterprise: Integrity
        doubleBookingResponse,
        feeCalculationsResponse,
        dataConsistencyResponse,
        // Enterprise: Negative Paths
        validationResponse,
        securityResponse,
        errorHandlingResponse,
        // Enterprise: Automation
        emailAutomationResponse,
        alertsResponse,
        retryLogicResponse,
        // Enterprise: Advanced
        widgetEmbedResponse,
        stressTestResponse,
        ciReadinessResponse,
        // Login & Permissions
        superEasyLoginResponse,
        promoCodePermissionsResponse,
        // Internal Booking System (TDD)
        internalBookingFeesResponse,
        internalBookingUIResponse,
        internalBookingDataResponse,
        internalBookingValidationResponse,
        internalBookingPaymentOptionsResponse,
        internalBookingModificationsResponse,
        internalBookingCancellationsResponse,
        // Session Fixes (Dec 2025)
        sessionEmailFormattingResponse,
        sessionPayNowRedirectResponse,
        sessionCapacityCalculationResponse,
        sessionUICalendarResponse,
        // Stripe Connect Sync & Refunds (Dec 2025)
        stripeConnectSyncResponse,
        refundSystemResponse,
        // Payment Security (CRITICAL - Dec 2025)
        paymentSecurityResponse,
        // Timeclock System (Dec 2025)
        timeclockSystemResponse,
        // 2FA Security (Dec 2025)
        twoFactorSecurityResponse,
        // Direct Charges, Refund Security, Multiple Refunds, Email Notifications (Dec 2025)
        directChargesResponse,
        refundSecurityResponse,
        multipleRefundsResponse,
        emailNotificationsResponse,
        // Data Migration (Dec 2025)
        dataMigrationResponse,
        // Advanced Security & Hardening (Dec 2025)
        rbacMatrixResponse,
        webhookSecurityResponse,
        bookingMutationsResponse,
        concurrencyResponse,
        piiSecurityResponse,
        tenantIsolationResponse,
        // Partners & Affiliates (Dec 2025)
        partnersAffiliatesResponse,
        partnerEarningsResponse,
        // Real-Time Availability (Dec 2025)
        realtimeAvailabilityResponse,
        // Widget Checkout Fixes (Dec 2025)
        widgetCheckoutFixesResponse,
        // Service Sharing (Dec 2025)
        serviceSharingResponse,
        // Widget JS Quality - iOS Safari Bug Fix (Dec 2025)
        widgetJsQualityResponse,
        // Platform Administration (Jan 2026)
        platformAdminResponse,
        // Cross-Service Availability (Jan 2026)
        crossServiceAvailabilityResponse,
        // Data Integrity (Jan 2026)
        dataIntegrityResponse,
        // Financials P&L (Jan 2026)
        financialsDataResponse,
        // Timezone Consistency (Jan 2026)
        timezoneConsistencyResponse,
        // Email Data Integrity (Jan 2026)
        emailDataIntegrityResponse
      ] = await Promise.all([
        // Foundation
        callEndpoint('org-lifecycle', tests.orgLifecycleTests),
        callEndpoint('multi-tenant', tests.multiTenantTests),
        callEndpoint('country-config', tests.countryConfigTests),
        callEndpoint('service-wizard', tests.serviceWizardTests),
        callEndpoint('booking-availability', tests.bookingAvailabilityTests),
        // Operations
        callEndpoint('roles', tests.rolesTests),
        callEndpoint('team', tests.teamTests),
        callEndpoint('assets', tests.assetsTests),
        callEndpoint('services', tests.servicesTests),
        callEndpoint('services-api', tests.servicesApiTests),
        callEndpoint('bookings', tests.bookingsTests),
        callEndpoint('custom-bookings', tests.customBookingTests),
        callEndpoint('custom-requests', tests.customRequestTests),
        callEndpoint('account-deletion', tests.accountDeletionTests),
        callEndpoint('flows', tests.flowsTests),
        // Financials
        callEndpoint('revenue', tests.revenueTests),
        callEndpoint('revenue-dashboard', tests.revenueDashboardTests),
        callEndpoint('payouts', tests.payoutsTests),
        callEndpoint('account', tests.accountTests),
        callEndpoint('payroll', tests.payrollTests),
        callEndpoint('promo-codes', tests.promoCodesTests),
        callEndpoint('stripe-connect', tests.stripeConnectTests),
        callEndpoint('expenses', tests.expensesTests),
        // Customers
        callEndpoint('branding', tests.brandingTests),
        callEndpoint('waivers', tests.waiversTests),
        callEndpoint('reviews', tests.reviewsTests),
        callEndpoint('checkin', tests.checkinTests),
        callEndpoint('leads', tests.leadsTests),
        callEndpoint('leads-consistency', tests.leadsConsistencyTests),
        callEndpoint('relations', tests.relationsTests),
        callEndpoint('tickets', tests.ticketsTests),
        // Settings
        callEndpoint('profile', tests.profileTests),
        callEndpoint('preferences', tests.preferencesTests),
        callEndpoint('calendar', tests.calendarTests),
        callEndpoint('webhooks', tests.webhooksTests),
        callEndpoint('api-keys', tests.apiKeysTests),
        callEndpoint('activity-log', tests.activityLogTests),
        callEndpoint('widget-api', tests.widgetApiTests),
        callEndpoint('currency-consistency', tests.currencyTests),
        callEndpoint('multi-currency-checkout', tests.multiCurrencyCheckoutTests),
        callEndpoint('session-addons-data', tests.addOnsDataTests),
        callEndpoint('session-customer-ticket', tests.customerTicketTests),
        callEndpoint('session-integrations', tests.integrationsTests),
        // Auth Flows
        callEndpoint('owner-signin', tests.ownerSigninTests),
        callEndpoint('team-signin', tests.teamSigninTests),
        callEndpoint('signup', tests.signupTests),
        // Customer Flows
        callEndpoint('widget-checkout', tests.widgetCheckoutTests),
        callEndpoint('waiver-flow', tests.waiverFlowTests),
        callEndpoint('qr-code-scanning', tests.qrCodeScanningTests),
        // Enterprise: Integrity
        callEndpoint('double-booking', tests.doubleBookingTests),
        callEndpoint('fee-calculations', tests.feeCalculationsTests),
        callEndpoint('data-consistency', tests.dataConsistencyTests),
        // Enterprise: Negative Paths
        callEndpoint('validation', tests.validationTests),
        callEndpoint('security', tests.securityTests),
        callEndpoint('error-handling', tests.errorHandlingTests),
        // Enterprise: Automation
        callEndpoint('email-automation', tests.emailAutomationTests),
        callEndpoint('alerts', tests.alertsTests),
        callEndpoint('retry-logic', tests.retryLogicTests),
        // Enterprise: Advanced
        callEndpoint('widget-embed', tests.widgetEmbedTests),
        callEndpoint('stress-test', tests.stressTestTests),
        callEndpoint('ci-readiness', tests.ciReadinessTests),
        // Login & Permissions
        callEndpoint('super-easy-login', tests.superEasyLoginTests),
        callEndpoint('promo-code-permissions', tests.promoCodePermissionsTests),
        // Internal Booking System (TDD - Expected to fail until features implemented)
        callEndpoint('internal-booking-fees', tests.internalBookingFeesTests),
        callEndpoint('internal-booking-ui', tests.internalBookingUITests),
        callEndpoint('internal-booking-data', tests.internalBookingDataTests),
        callEndpoint('internal-booking-validation', tests.internalBookingValidationTests),
        callEndpoint('internal-booking-payment-options', tests.internalBookingPaymentOptionsTests),
        callEndpoint('internal-booking-modifications', tests.internalBookingModificationsTests),
        callEndpoint('internal-booking-cancellations', tests.internalBookingCancellationsTests),
        // Session Fixes (Dec 2025)
        callEndpoint('session-email-formatting', tests.sessionEmailFormattingTests),
        callEndpoint('session-pay-now-redirect', tests.sessionPayNowRedirectTests),
        callEndpoint('session-capacity-calculation', tests.sessionCapacityCalculationTests),
        callEndpoint('session-ui-calendar', tests.sessionUICalendarTests),
        // Stripe Connect Sync & Refunds (Dec 2025)
        callEndpoint('stripe-connect-sync', tests.stripeConnectSyncTests),
        callEndpoint('refund-system', tests.refundSystemTests),
        // Payment Security (CRITICAL - Dec 2025)
        callEndpoint('payment-security', tests.paymentSecurityTests),
        // Timeclock System (Dec 2025)
        callEndpoint('timeclock-system', tests.timeclockSystemTests),
        // 2FA Security (Dec 2025)
        callEndpoint('2fa-security', tests.twoFactorSecurityTests),
        // Direct Charges, Refund Security, Multiple Refunds, Email Notifications (Dec 2025)
        callEndpoint('direct-charges', tests.directChargesTests),
        callEndpoint('refund-security', tests.refundSecurityTests),
        callEndpoint('multiple-refunds', tests.multipleRefundsTests),
        callEndpoint('email-notifications', tests.emailNotificationsTests),
        // Data Migration (Dec 2025)
        callEndpoint('data-migration', tests.dataMigrationTests),
        // Advanced Security & Hardening (Dec 2025)
        callEndpoint('rbac-matrix', tests.rbacMatrixTests),
        callEndpoint('webhook-security', tests.webhookSecurityTests),
        callEndpoint('booking-mutations', tests.bookingMutationsTests),
        callEndpoint('concurrency', tests.concurrencyTests),
        callEndpoint('pii-security', tests.piiSecurityTests),
        callEndpoint('tenant-isolation', tests.tenantIsolationTests),
        // Partners & Affiliates (Dec 2025)
        callEndpoint('partners-affiliates', tests.partnersAffiliatesTests),
        callEndpoint('partner-earnings', tests.partnerEarningsTests),
        // Real-Time Availability (Dec 2025)
        callEndpoint('realtime-availability', tests.realtimeAvailabilityTests),
        // Widget Checkout (Dec 2025)
        callEndpoint('widget-checkout-fixes', tests.widgetCheckoutFixesTests),
        // Service Sharing (Dec 2025)
        callEndpoint('service-sharing', tests.serviceSharingTests),
        // Widget JS Quality - iOS Safari Bug Fix (Dec 2025)
        callEndpoint('widget-js-quality', tests.widgetJsQualityTests),
        // Platform Administration (Jan 2026)
        callEndpoint('platform-admin', tests.platformAdminTests),
        // Cross-Service Availability (Jan 2026)
        callEndpoint('cross-service-availability', crossServiceAvailabilityTests),
        // Data Integrity (Jan 2026)
        callEndpoint('data-integrity', dataIntegrityTests),
        // Financials P&L (Jan 2026)
        callEndpoint('financials', financialsDataTests),
        // Timezone Consistency (Jan 2026)
        callEndpoint('timezone-consistency', timezoneConsistencyTests),
        // Email Data Integrity (Jan 2026)
        callEndpoint('email-data-integrity', emailDataIntegrityTests)
      ]);
      
      setTestResults({
        // Foundation
        orgLifecycle: orgLifecycleResponse?.data?.tests || [],
        multiTenant: multiTenantResponse?.data?.tests || [],
        countryConfig: countryConfigResponse?.data?.tests || [],
        serviceWizard: serviceWizardResponse?.data?.tests || [],
        bookingAvailability: bookingAvailabilityResponse?.data?.results || [],
        // Operations
        roles: rolesResponse?.data?.tests || [],
        team: teamResponse?.data?.tests || [],
        assets: assetsResponse?.data?.tests || [],
        services: servicesResponse?.data?.tests || [],
        servicesApi: servicesApiResponse?.data?.tests || [],
        bookings: bookingsResponse?.data?.tests || [],
        customBookings: customBookingsResponse?.data?.tests || [],
        customRequests: customRequestsResponse?.data?.tests || [],
        accountDeletion: accountDeletionResponse?.data?.tests || [],
        flows: flowsResponse?.data?.tests || [],
        // Financials
        revenue: revenueResponse?.data?.tests || [],
        revenueDashboard: revenueDashboardResponse?.data?.tests || [],
        payouts: payoutsResponse?.data?.tests || [],
        account: accountResponse?.data?.tests || [],
        payroll: payrollResponse?.data?.tests || [],
        promoCodes: promoCodesResponse?.data?.tests || [],
        stripeConnect: stripeConnectResponse?.data?.tests || [],
        expenses: expensesResponse?.data?.tests || [],
        // Customers
        branding: brandingResponse?.data?.tests || [],
        waivers: waiversResponse?.data?.tests || [],
        reviews: reviewsResponse?.data?.tests || [],
        checkin: checkinResponse?.data?.tests || [],
        leads: leadsResponse?.data?.tests || [],
        'leads-consistency': leadsConsistencyResponse?.data?.tests || [],
        relations: relationsResponse?.data?.tests || [],
        tickets: ticketsResponse?.data?.tests || [],
        // Settings
        profile: profileResponse?.data?.tests || [],
        preferences: preferencesResponse?.data?.tests || [],
        calendar: calendarResponse?.data?.tests || [],
        webhooks: webhooksResponse?.data?.tests || [],
        apiKeys: apiKeysResponse?.data?.tests || [],
        activityLog: activityLogResponse?.data?.tests || [],
        widgetApi: widgetApiResponse?.data?.tests || [],
        currencyConsistency: currencyResponse?.data?.tests || [],
        multiCurrencyCheckout: multiCurrencyCheckoutResponse?.data?.tests || [],
        addOnsData: addOnsDataResponse?.data?.tests || [],
        customerTicket: customerTicketResponse?.data?.tests || [],
        integrations: integrationsResponse?.data?.tests || [],
        // Auth Flows
        ownerSignin: ownerSigninResponse?.data?.tests || [],
        teamSignin: teamSigninResponse?.data?.tests || [],
        signup: signupResponse?.data?.tests || [],
        // Customer Flows
        widgetCheckout: widgetCheckoutResponse?.data?.tests || [],
        waiverFlow: waiverFlowResponse?.data?.tests || [],
        qrCodeScanning: qrCodeScanningResponse?.data?.tests || [],
        // Enterprise: Integrity
        doubleBooking: doubleBookingResponse?.data?.tests || [],
        feeCalculations: feeCalculationsResponse?.data?.tests || [],
        dataConsistency: dataConsistencyResponse?.data?.tests || [],
        // Enterprise: Negative Paths
        validation: validationResponse?.data?.tests || [],
        security: securityResponse?.data?.tests || [],
        errorHandling: errorHandlingResponse?.data?.tests || [],
        // Enterprise: Automation
        emailAutomation: emailAutomationResponse?.data?.tests || [],
        alerts: alertsResponse?.data?.tests || [],
        retryLogic: retryLogicResponse?.data?.tests || [],
        // Enterprise: Advanced
        widgetEmbed: widgetEmbedResponse?.data?.tests || [],
        stressTest: stressTestResponse?.data?.tests || [],
        ciReadiness: ciReadinessResponse?.data?.tests || [],
        // Login & Permissions
        superEasyLogin: superEasyLoginResponse?.data?.tests || [],
        promoCodePermissions: promoCodePermissionsResponse?.data?.tests || [],
        // Internal Booking System (TDD)
        internalBookingFees: internalBookingFeesResponse?.data?.tests || [],
        internalBookingUI: internalBookingUIResponse?.data?.tests || [],
        internalBookingData: internalBookingDataResponse?.data?.tests || [],
        internalBookingValidation: internalBookingValidationResponse?.data?.tests || [],
        internalBookingPaymentOptions: internalBookingPaymentOptionsResponse?.data?.tests || [],
        internalBookingModifications: internalBookingModificationsResponse?.data?.tests || [],
        internalBookingCancellations: internalBookingCancellationsResponse?.data?.tests || [],
        // Session Fixes (Dec 2025)
        sessionEmailFormatting: sessionEmailFormattingResponse?.data?.tests || [],
        sessionPayNowRedirect: sessionPayNowRedirectResponse?.data?.tests || [],
        sessionCapacityCalculation: sessionCapacityCalculationResponse?.data?.tests || [],
        sessionUICalendar: sessionUICalendarResponse?.data?.tests || [],
        // Stripe Connect Sync & Refunds (Dec 2025)
        stripeConnectSync: stripeConnectSyncResponse?.data?.tests || [],
        refundSystem: refundSystemResponse?.data?.tests || [],
        // Payment Security (CRITICAL - Dec 2025)
        paymentSecurity: paymentSecurityResponse?.data?.tests || [],
        // Timeclock System (Dec 2025)
        timeclockSystem: timeclockSystemResponse?.data?.tests || timeclockSystemResponse?.data?.results || [],
        // 2FA Security (Dec 2025)
        twoFactorSecurity: twoFactorSecurityResponse?.data?.tests || twoFactorSecurityResponse?.data?.results || [],
        // Direct Charges, Refund Security, Multiple Refunds, Email Notifications (Dec 2025)
        directCharges: directChargesResponse?.data?.tests || directChargesResponse?.data?.results || [],
        refundSecurity: refundSecurityResponse?.data?.tests || refundSecurityResponse?.data?.results || [],
        multipleRefunds: multipleRefundsResponse?.data?.tests || multipleRefundsResponse?.data?.results || [],
        emailNotifications: emailNotificationsResponse?.data?.tests || emailNotificationsResponse?.data?.results || [],
        // Data Migration (Dec 2025)
        dataMigration: dataMigrationResponse?.data?.tests || dataMigrationResponse?.data?.results || [],
        // Advanced Security & Hardening (Dec 2025)
        rbacMatrix: rbacMatrixResponse?.data?.tests || rbacMatrixResponse?.data?.results || [],
        webhookSecurity: webhookSecurityResponse?.data?.tests || webhookSecurityResponse?.data?.results || [],
        bookingMutations: bookingMutationsResponse?.data?.tests || bookingMutationsResponse?.data?.results || [],
        concurrency: concurrencyResponse?.data?.tests || concurrencyResponse?.data?.results || [],
        piiSecurity: piiSecurityResponse?.data?.tests || piiSecurityResponse?.data?.results || [],
        tenantIsolation: tenantIsolationResponse?.data?.tests || tenantIsolationResponse?.data?.results || [],
        // Partners & Affiliates (Dec 2025)
        partnersAffiliates: partnersAffiliatesResponse?.data?.tests || partnersAffiliatesResponse?.data?.results || [],
        partnerEarnings: partnerEarningsResponse?.data?.tests || partnerEarningsResponse?.data?.results || [],
        // Real-Time Availability (Dec 2025)
        realtimeAvailability: realtimeAvailabilityResponse?.data?.tests || realtimeAvailabilityResponse?.data?.results || [],
        // Widget Checkout Fixes (Dec 2025)
        widgetCheckoutFixes: widgetCheckoutFixesResponse?.data?.tests || widgetCheckoutFixesResponse?.data?.results || [],
        // Service Sharing (Dec 2025)
        serviceSharing: serviceSharingResponse?.data?.tests || serviceSharingResponse?.data?.results || [],
        // Widget JS Quality - iOS Safari Bug Fix (Dec 2025)
        widgetJsQuality: widgetJsQualityResponse?.data?.tests || widgetJsQualityResponse?.data?.results || [],
        // Platform Administration (Jan 2026)
        platformAdmin: platformAdminResponse?.data?.tests || platformAdminResponse?.data?.results || [],
        // Cross-Service Availability (Jan 2026)
        crossServiceAvailability: crossServiceAvailabilityResponse?.data?.tests || crossServiceAvailabilityResponse?.data?.results || [],
        // Data Integrity (Jan 2026)
        dataIntegrity: dataIntegrityResponse?.data?.tests || dataIntegrityResponse?.data?.results || [],
        // Financials P&L (Jan 2026)
        financialsData: financialsDataResponse?.data?.tests || financialsDataResponse?.data?.results || [],
        // Timezone Consistency (Jan 2026)
        timezoneConsistency: timezoneConsistencyResponse?.data?.tests || timezoneConsistencyResponse?.data?.results || [],
        // Email Data Integrity (Jan 2026)
        emailDataIntegrity: emailDataIntegrityResponse?.data?.tests || emailDataIntegrityResponse?.data?.results || []
      });
      
      // Store org info if available
      if (rolesResponse.data.testOrg) {
        setTestOrgInfo({
          name: rolesResponse.data.testOrg,
          slug: rolesResponse.data.testOrgSlug
        });
      }
      
      setLastChecked(new Date());
      
      // Calculate totals - ALL test responses must be included here
      const allResponses = [
        // Foundation
        orgLifecycleResponse, multiTenantResponse, countryConfigResponse, serviceWizardResponse, bookingAvailabilityResponse,
        // Operations
        rolesResponse, teamResponse, assetsResponse, servicesResponse, servicesApiResponse, bookingsResponse, flowsResponse,
        // Financials
        revenueResponse, revenueDashboardResponse, payoutsResponse, accountResponse, payrollResponse, promoCodesResponse, stripeConnectResponse, expensesResponse,
        // Customers
        brandingResponse, waiversResponse, reviewsResponse, checkinResponse, leadsResponse, relationsResponse, ticketsResponse,
        // Settings
        profileResponse, preferencesResponse, calendarResponse, webhooksResponse, apiKeysResponse, activityLogResponse, widgetApiResponse, currencyResponse,
        // Auth Flows
        ownerSigninResponse, teamSigninResponse, signupResponse,
        // Customer Flows
        widgetCheckoutResponse, waiverFlowResponse, qrCodeScanningResponse,
        // Enterprise: Integrity
        doubleBookingResponse, feeCalculationsResponse, dataConsistencyResponse,
        // Enterprise: Negative Paths
        validationResponse, securityResponse, errorHandlingResponse,
        // Enterprise: Automation
        emailAutomationResponse, alertsResponse, retryLogicResponse,
        // Enterprise: Advanced
        widgetEmbedResponse, stressTestResponse, ciReadinessResponse,
        // Login & Permissions
        superEasyLoginResponse, promoCodePermissionsResponse,
        // Internal Booking System (TDD)
        internalBookingFeesResponse, internalBookingUIResponse, internalBookingDataResponse,
        internalBookingValidationResponse, internalBookingPaymentOptionsResponse,
        internalBookingModificationsResponse, internalBookingCancellationsResponse,
        // Session Fixes (Dec 2025)
        sessionEmailFormattingResponse, sessionPayNowRedirectResponse,
        sessionCapacityCalculationResponse, sessionUICalendarResponse,
        // Stripe Connect Sync & Refunds (Dec 2025)
        stripeConnectSyncResponse, refundSystemResponse,
        // Payment Security (CRITICAL - Dec 2025)
        paymentSecurityResponse,
        // Timeclock System (Dec 2025)
        timeclockSystemResponse,
        // 2FA Security (Dec 2025)
        twoFactorSecurityResponse,
        // Direct Charges, Refund Security, Multiple Refunds, Email Notifications, Data Migration (Dec 2025)
        directChargesResponse, refundSecurityResponse, multipleRefundsResponse, emailNotificationsResponse, dataMigrationResponse,
        // Advanced Security & Hardening (Dec 2025)
        rbacMatrixResponse, webhookSecurityResponse, bookingMutationsResponse, concurrencyResponse, piiSecurityResponse, tenantIsolationResponse,
        // Partners & Affiliates (Dec 2025)
        partnersAffiliatesResponse, partnerEarningsResponse,
        // Real-Time Availability (Dec 2025)
        realtimeAvailabilityResponse,
        // Widget Checkout Fixes (Dec 2025)
        widgetCheckoutFixesResponse,
        // Service Sharing (Dec 2025)
        serviceSharingResponse,
        // Widget JS Quality - iOS Safari Bug Fix (Dec 2025)
        widgetJsQualityResponse,
        // Platform Administration (Jan 2026)
        platformAdminResponse,
        // Cross-Service Availability (Jan 2026)
        crossServiceAvailabilityResponse,
        // Data Integrity (Jan 2026)
        dataIntegrityResponse,
        // Financials P&L (Jan 2026)
        financialsDataResponse,
        // Timezone Consistency (Jan 2026)
        timezoneConsistencyResponse,
        // Email Data Integrity (Jan 2026)
        emailDataIntegrityResponse
      ];
      
      // Count tests the same way ProgressChart does - from actual test result arrays
      const allTestResults = [
        // Foundation
        ...(testResults.orgLifecycle || []),
        ...(testResults.multiTenant || []),
        ...(testResults.countryConfig || []), ...(testResults.serviceWizard || []), ...(testResults.bookingAvailability || []),
        ...(testResults.serviceWizard || []),
        ...(testResults.bookingAvailability || []),
        // Operations
        ...testResults.roles,
        ...testResults.team,
        ...testResults.assets,
        ...testResults.services,
        ...(testResults.servicesApi || []),
        ...testResults.bookings,
        ...testResults.flows,
        // Financials
        ...testResults.revenue,
        ...(testResults.revenueDashboard || []),
        ...testResults.payouts,
        ...testResults.account,
        ...testResults.payroll,
        ...(testResults.promoCodes || []),
        ...testResults.stripeConnect,
        ...(testResults.expenses || []),
        // Customers
        ...testResults.branding,
        ...testResults.waivers,
        ...testResults.reviews,
        ...testResults.checkin,
        ...testResults.leads,
        ...testResults.relations,
        ...(testResults.tickets || []),
        // Settings
        ...testResults.profile,
        ...testResults.preferences,
        ...testResults.calendar,
        ...testResults.webhooks,
        ...testResults.apiKeys,
        ...testResults.activityLog, ...(testResults.widgetApi || []), ...(testResults.currencyConsistency || []), ...(testResults.multiCurrencyCheckout || []), ...(testResults.addOnsData || []), ...(testResults.customerTicket || []), ...(testResults.integrations || []),
        // Auth Flows
        ...testResults.ownerSignin,
        ...testResults.teamSignin,
        ...testResults.signup,
        // Customer Flows
        ...testResults.widgetCheckout,
        ...testResults.waiverFlow,
        ...testResults.qrCodeScanning,
        // Enterprise: Integrity
        ...(testResults.doubleBooking || []),
        ...(testResults.feeCalculations || []),
        ...(testResults.dataConsistency || []),
        // Enterprise: Negative Paths
        ...(testResults.validation || []),
        ...(testResults.security || []),
        ...(testResults.errorHandling || []),
        // Enterprise: Automation
        ...(testResults.emailAutomation || []),
        ...(testResults.alerts || []),
        ...(testResults.retryLogic || []),
        // Enterprise: Advanced
        ...(testResults.widgetEmbed || []),
        ...(testResults.stressTest || []),
        ...(testResults.ciReadiness || []),
        // Login & Permissions
        ...(testResults.superEasyLogin || []),
        ...(testResults.promoCodePermissions || []),
        // Internal Booking System (TDD)
        ...(testResults.internalBookingFees || []),
        ...(testResults.internalBookingUI || []),
        ...(testResults.internalBookingData || []),
        ...(testResults.internalBookingValidation || []),
        ...(testResults.internalBookingPaymentOptions || []),
        ...(testResults.internalBookingModifications || []),
        ...(testResults.internalBookingCancellations || []),
        // Session Fixes (Dec 2025)
        ...(testResults.sessionEmailFormatting || []),
        ...(testResults.sessionPayNowRedirect || []),
        ...(testResults.sessionCapacityCalculation || []),
        ...(testResults.sessionUICalendar || []),
        // Stripe Connect Sync & Refunds (Dec 2025)
        ...(testResults.stripeConnectSync || []),
        ...(testResults.refundSystem || []),
        // Payment Security (CRITICAL - Dec 2025)
        ...(testResults.paymentSecurity || []),
        // Timeclock System (Dec 2025)
        ...(testResults.timeclockSystem || []),
        // 2FA Security (Dec 2025)
        ...(testResults.twoFactorSecurity || []),
        // Direct Charges, Refund Security, Multiple Refunds, Email Notifications, Data Migration (Dec 2025)
        ...(testResults.directCharges || []),
        ...(testResults.refundSecurity || []),
        ...(testResults.multipleRefunds || []),
        ...(testResults.emailNotifications || []),
        ...(testResults.dataMigration || []),
        // Advanced Security & Hardening (Dec 2025)
        ...(testResults.rbacMatrix || []),
        ...(testResults.webhookSecurity || []),
        ...(testResults.bookingMutations || []),
        ...(testResults.concurrency || []),
        ...(testResults.piiSecurity || []),
        ...(testResults.tenantIsolation || []),
        // Partners & Affiliates (Dec 2025)
        ...(testResults.partnersAffiliates || []),
        ...(testResults.partnerEarnings || []),
        // Real-Time Availability (Dec 2025)
        ...(testResults.realtimeAvailability || []),
        // Widget Checkout Fixes (Dec 2025)
        ...(testResults.widgetCheckoutFixes || []),
        // Service Sharing (Dec 2025)
        ...(testResults.serviceSharing || []),
        // Widget JS Quality - iOS Safari Bug Fix (Dec 2025)
        ...(testResults.widgetJsQuality || []),
        // Platform Administration (Jan 2026)
        ...(testResults.platformAdmin || [])
      ];
      
      const totalPassed = allTestResults.filter(t => t.status === 'pass').length;
      const totalFailed = allTestResults.filter(t => t.status === 'fail').length;
      const totalWarnings = allTestResults.filter(t => t.status === 'warn').length;
      const totalTests = allTestResults.length;
      
      // Calculate test duration
      const endTime = performance.now();
      const durationSeconds = ((endTime - startTime) / 1000).toFixed(2);
      setTestDuration(durationSeconds);
      
      // Save results to backend for weekly email reports
      try {
        const token = localStorage.getItem('jwtToken');
        const activeIssues = allTestResults
          .filter(t => t.status === 'fail' || t.status === 'warn')
          .slice(0, 20)
          .map(t => ({ name: t.name, status: t.status, message: t.message }));
        
        await axios.post(`${API_URL}/health/save-results`, {
          passed: totalPassed,
          failed: totalFailed,
          warnings: totalWarnings,
          total: totalTests,
          duration: durationSeconds,
          testOrg: selectedTestOrg,
          activeIssues: activeIssues
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.log('Could not save test results:', err.message);
      }
      
      if (totalFailed === 0) {
        toast.success(`Tests finished! All passed in ${durationSeconds}s`);
      } else {
        toast.info(`Tests finished in ${durationSeconds}s`);
      }
    } catch (error) {
      console.error("Health check error:", error);
      toast.error("Failed to run tests");
    } finally {
      setLoading(false);
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('Delete all test organizations and data? This cannot be undone.')) {
      return;
    }
    
    setCleanupLoading(true);
    toast.info('Cleaning up test data...');
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${API_URL}/health/cleanup-test-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const summary = response.data.summary;
        toast.success(
          `Cleanup complete! Deleted ${summary.organizations_deleted} org(s) and ${summary.databases_dropped} database(s)`
        );
        
        // Clear test org info
        setTestOrgInfo(null);
        setSelectedTestOrg(null);
        
        // Reload org list
        await loadTestOrgs();
      }
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error("Failed to cleanup test data");
    } finally {
      setCleanupLoading(false);
    }
  };

  // Get selected org info for display
  const selectedOrgData = testOrgsList.find(org => org.slug === selectedTestOrg);

  return (
    <TabPageLayout
      title="System Health"
      subtitle="Multi-tenant testing with data persistence"
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={runHealthCheck}
            disabled={!selectedTestOrg || isOperationRunning}
            className="bg-blue-600 hover:bg-blue-700 text-white h-10"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Activity className="w-4 h-4 mr-2" />
                Run Tests
              </>
            )}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-10"
                disabled={!lastChecked}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportResults('json')}>
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportResults('csv')}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportResults('pdf')}>
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Control Panel */}
        <DataCard>
          <div className="space-y-4">
            {/* Test Org Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-medium text-gray-700 min-w-[80px]">
                Test Org:
              </label>
              <select
                value={selectedTestOrg || ''}
                onChange={(e) => setSelectedTestOrg(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isOperationRunning}
              >
                <option value="">Select test organization...</option>
                {testOrgsList.map(org => (
                  <option key={org.slug} value={org.slug}>
                    {org.name} ({org.slug})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Selected Org Data Counts */}
            {selectedOrgData && (
              <p className="text-xs text-gray-500 sm:pl-[92px]">
                {formatDataCounts(selectedOrgData.dataCounts)}
              </p>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={createTestOrg}
                disabled={isOperationRunning}
                className="bg-blue-600 hover:bg-blue-700 text-white h-9"
              >
                {createOrgLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Building2 className="w-4 h-4 mr-2" />
                    Create Test Org
                  </>
                )}
              </Button>
              
              <Button
                onClick={addTestData}
                disabled={!selectedTestOrg || isOperationRunning}
                variant="outline"
                className="h-9 border-green-300 text-green-700 hover:bg-green-50"
              >
                {addDataLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Data
                  </>
                )}
              </Button>
              
              <Button
                onClick={testDeleteOperations}
                disabled={!selectedTestOrg || isOperationRunning}
                variant="outline"
                className="h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {deleteTestLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Test Deletes
                  </>
                )}
              </Button>
              
              <Button
                onClick={cleanupTestData}
                disabled={isOperationRunning}
                variant="outline"
                className="h-9 border-red-300 text-red-600 hover:bg-red-50"
              >
                {cleanupLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Eraser className="w-4 h-4 mr-2" />
                    Cleanup All
                  </>
                )}
              </Button>
            </div>
            
            {/* Workflow Info */}
            <p className="text-xs text-gray-500">
              Workflow: Create Org → Select → Run Tests → Inspect → Test Deletes → Cleanup
            </p>
          </div>
        </DataCard>

      {/* Last Checked with Timezone & Progress Chart */}
      {lastChecked && (
        <DataCard>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-gray-500">
              Last checked: {formatTimestamp(lastChecked)}
            </p>
            {testDuration && (
              <div className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg px-3 py-1.5">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-semibold text-blue-700">{testDuration}s</span>
                <span className="text-xs text-blue-600">runtime</span>
              </div>
            )}
          </div>
          
          {/* Active Issues Box */}
          <ActiveIssuesBox testResults={testResults} />
          
          <ProgressChart testResults={testResults} testOrgInfo={testOrgInfo} />
        </DataCard>
      )}

      {/* Pre-Flight System Status - ALWAYS VISIBLE */}
      <PreFlightStatus />

      {/* Foundation Tests - Org Lifecycle & Multi-Tenant */}
      <FoundationSection testResults={testResults} />

      {/* Test Sections */}
      <OperationsSection testResults={testResults} />
      <EmployeesSection testResults={testResults} />
      <FinancialsSection testResults={testResults} />
      <CustomersSection testResults={testResults} />
      <SettingsSection testResults={testResults} />
      <AuthFlowsSection testResults={testResults} />
      <CustomerFlowsSection testResults={testResults} />
      
      {/* Enterprise Test Sections */}
      <IntegritySection testResults={testResults} />
      <NegativePathSection testResults={testResults} />
      <AutomationSection testResults={testResults} />
      <AdvancedSection testResults={testResults} />
      
      {/* Security & Hardening Tests */}
      <SecurityHardeningSection testResults={testResults} />
      
      {/* Partners & Affiliates Tests */}
      <PartnersAffiliatesSection testResults={testResults} />
      
      {/* Real-Time Availability Tests (Dec 2025) */}
      <RealtimeAvailabilitySection testResults={testResults} />
      
      {/* Widget Checkout Tests (Dec 2025) */}
      <WidgetCheckoutSection testResults={testResults} />
      
      {/* Widget JS Quality Tests (Dec 2025 - iOS Safari Bug Fix) */}
      <WidgetJsQualitySection testResults={testResults} />
      
      {/* Service Sharing Tests (Dec 2025) */}
      <ServiceSharingSection testResults={testResults} />
      
      {/* Platform Administration Tests (Jan 2026) */}
      <PlatformAdminSection testResults={testResults} />
      
      {/* Data Integrity & Audit Tests (Jan 2026) */}
      <DataIntegrityAuditSection testResults={testResults} />
      
      {/* Login & Permissions Tests */}
      <LoginSection testResults={testResults} />
      </div>
    </TabPageLayout>
  );
};

export default SystemHealthTab;
